import { getDefaultStore } from "jotai";

import {
  activeSlotAtom,
  hangingProtocolActionsAtom,
  maximizedSlotAtom,
  slotAssignmentsSelector,
  slotStateFamily,
} from "../../atoms/hangingProtocolAtoms";
import { aiJumpTargetAtom } from "../../atoms/positionAtoms";
import { selectionStateAtom, studyMetadataAtom } from "../../atoms/selectionAtoms";
import { loadImagesWithConcurrency } from "../../utils/dicomLoader";
import { buildS3ImageIds } from "../../utils/dicomMetadataLoader";
import { DEFAULT_SLOT, SLOT_IDS, type SlotId } from "../../utils/hangingProtocolService";
import { logger } from "../../utils/logger";
import { warmJumpTarget } from "../../utils/warmJumpTarget";
import { emit } from "../viewerBus";
import { registerCommand } from "./registry";

// Fix A: the instant a finding is clicked, warm a small target-first window of
// the destination series so the clicked slice is a cache-hit when the volume/
// stack loads (instead of waiting for the series to stream 0->N).
const JUMP_WARM_FRAMES = 24;
const JUMP_WARM_CONCURRENCY = 8;
let jumpWarmAbort: AbortController | null = null;

registerCommand("selectFinding", ({ finding, target, setSeriesOptions, locate = true, window }) => {
  const store = getDefaultStore();

  const selection = store.get(selectionStateAtom);
  if (finding && selection.selectedFindingCondition === finding) {
    store.set(selectionStateAtom, { activeGroupId: null, selectedFindingCondition: null });
    return;
  }

  if (finding) {
    store.set(selectionStateAtom, { activeGroupId: null, selectedFindingCondition: finding });
  }

  if (!target) return;

  const { seriesId, sliceIndex, frameOfReferenceUid } = target;

  // Broadcast the AI target's world coordinate to every volume viewport so
  // cross-plane views (axial / sag / cor) snap to the same anatomy, not
  // just the priority series's slot. IPP comes straight from canonical
  // metadata — same source the backend uses to mint sliceIndex. FoR
  // comes from the payload (sourced server-side on the same response).
  const studyMetadata = store.get(studyMetadataAtom);
  const targetInstance = studyMetadata?.series?.[seriesId]?.[sliceIndex];
  const ipp = targetInstance?.image_position_patient;
  if (ipp && ipp.length === 3) {
    const prevTrigger = store.get(aiJumpTargetAtom)?.trigger ?? 0;
    store.set(aiJumpTargetAtom, {
      ipp: [ipp[0], ipp[1], ipp[2]],
      seriesId,
      sliceIndex,
      frameOfReferenceUid,
      centerWorld: target.worldCentroid ?? [ipp[0], ipp[1], ipp[2]],
      locate,
      pixelCentroid: target.pixelCentroid,
      segmentIndex: target.segmentIndex,
      trigger: prevTrigger + 1,
    });
  } else {
    // Silent skip: the cross-viewport jump path is gated on a valid IPP.
    // Missing IPP means the canonical-metadata row for this instance lacks
    // `image_position_patient` (unbackfilled, transcoded, or the seriesId/
    // sliceIndex pair doesn't resolve). The slice update + slot activate
    // below still fires so the priority series moves to the right slice;
    // only the cross-plane sync is suppressed.
    logger.warn(
      "select_finding ipp_missing seriesId=%s sliceIndex=%d hasInstance=%s",
      seriesId,
      sliceIndex,
      targetInstance ? "true" : "false",
    );
  }

  const slotAssignments = store.get(slotAssignmentsSelector);
  const existingSlot = SLOT_IDS.find((id) => slotAssignments[id] === seriesId);

  // In the 1+3 spotlight layout the maximized slot is the "main" (large)
  // viewport, so a clicked finding belongs there — not in whatever small slot
  // happens to already hold its series. Route the jump to the maximized slot:
  // reuse it in place when it's already showing this series, otherwise swap the
  // series into it below (SET_SERIES on `slotId`). Outside spotlight mode
  // (no maximized slot) behavior is unchanged: reuse the slot already showing
  // the series, else load into the default slot.
  const maximizedSlot = store.get(maximizedSlotAtom) as SlotId | null;
  const reuseSlot = maximizedSlot
    ? slotAssignments[maximizedSlot] === seriesId
      ? maximizedSlot
      : null
    : existingSlot;

  if (reuseSlot) {
    const slot = store.get(slotStateFamily(reuseSlot));
    if (slot.totalSlices > 0 && (sliceIndex < 0 || sliceIndex >= slot.totalSlices)) {
      emit("toast", { message: `Finding's slice is out of range (1–${slot.totalSlices})` });
      return;
    }
    store.set(hangingProtocolActionsAtom, {
      type: "CHANGE_SLICE",
      slotId: reuseSlot,
      sliceIndex,
    });
    if (window)
      store.set(hangingProtocolActionsAtom, {
        type: "CHANGE_WINDOWING",
        slotId: reuseSlot,
        ...window,
      });
    store.set(activeSlotAtom, reuseSlot);
    return;
  }

  // Fix A: kick off the target-first warm now, before the viewport's own load
  // effect even runs. Fire-and-forget + de-duped by cornerstone's loader; a new
  // finding-click cancels the previous warm so we don't pile up windows.
  const cdn = studyMetadata?.canonical_cdn_base_url;
  const targetInstances = studyMetadata?.series?.[seriesId];
  if (cdn && targetInstances?.length) {
    jumpWarmAbort?.abort();
    jumpWarmAbort = new AbortController();
    const warmSignal = jumpWarmAbort.signal;
    void warmJumpTarget({
      imageIds: buildS3ImageIds(cdn, targetInstances),
      sliceIndex,
      frames: JUMP_WARM_FRAMES,
      load: (warmIds) =>
        loadImagesWithConcurrency(warmIds, JUMP_WARM_CONCURRENCY, undefined, warmSignal),
    }).catch((e) => {
      if (warmSignal.aborted) return;
      logger.debug("select_finding jump_warm_failed seriesId=%s err=%o", seriesId, e);
    });
  }

  // Swap the finding's series into the destination slot. In the 1+3 spotlight
  // layout that is the maximized (main) slot, so the jump lands in the large
  // viewport; otherwise it's the default slot (the pre-spotlight behavior).
  const slotId = maximizedSlot ?? DEFAULT_SLOT;
  // Seed the target slice into the swap itself (pendingJump) rather than
  // a requestAnimationFrame'd CHANGE_SLICE. The rAF raced StackViewport's async
  // setStack: when the finding's series wasn't already displayed, the weak-GPU
  // stack fallback loaded the new series at its middle slice and the late
  // CHANGE_SLICE was clobbered, so the jump silently landed mid-series. Setting
  // both atomically means the viewport honors the target as soon as it loads.
  // (Mike Moritz, 2026-05-29.)
  store.set(hangingProtocolActionsAtom, {
    type: "SET_SERIES",
    slotId,
    seriesId,
    sliceIndex,
    windowing: window ?? setSeriesOptions?.windowing,
    isInverted: setSeriesOptions?.isInverted,
  });
  store.set(activeSlotAtom, slotId);
});
